import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Clock, Users, CalendarDays, TrendingUp, RefreshCw, Loader2, Download, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const HOUR_NOTE = "Each confirmed booking = booking duration (1h) + 1h setup/travel overhead";

const COLORS = ["#15803d","#0369a1","#6d28d9","#334155","#b45309","#0d9488","#dc2626","#7c3aed"];

const VH_CHART_MARGIN = { top: 4, right: 8, left: -16, bottom: 0 };
const VH_AXIS_TICK = { fontSize: 11, fill: "#94a3b8" };
const VH_TOOLTIP_CURSOR = { fill: "#f8fafc" };
const VH_BAR_RADIUS = [4, 4, 0, 0];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-sm min-w-40">
      <p className="font-semibold text-slate-700 mb-2">{label || d.name}</p>
      {d.total_hours !== undefined && (
        <>
          <div className="flex justify-between gap-4 py-0.5 text-xs">
            <span className="text-slate-500">Confirmed bookings</span>
            <span className="font-semibold text-slate-800">{d.confirmed}</span>
          </div>
          <div className="flex justify-between gap-4 py-0.5 text-xs">
            <span className="text-slate-500">Booking time</span>
            <span className="font-semibold text-slate-800">{d.booking_hours}h</span>
          </div>
          <div className="flex justify-between gap-4 py-0.5 text-xs">
            <span className="text-slate-500">Setup/travel</span>
            <span className="font-semibold text-slate-800">{d.overhead_hours}h</span>
          </div>
          <div className="flex justify-between gap-4 pt-1 mt-1 border-t border-slate-100 text-xs">
            <span className="text-slate-700 font-semibold">Total hours</span>
            <span className="font-bold text-green-700">{d.total_hours}h</span>
          </div>
        </>
      )}
      {d.total_hours === undefined && payload[0].value !== undefined && (
        <div className="flex justify-between gap-4 py-0.5 text-xs">
          <span className="text-slate-500">Total hours</span>
          <span className="font-bold text-green-700">{payload[0].value}h</span>
        </div>
      )}
    </div>
  );
};

const MonthlyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      <div className="flex justify-between gap-4 text-xs">
        <span className="text-slate-500">Total hours</span>
        <span className="font-bold text-teal-700">{payload[0].value}h</span>
      </div>
    </div>
  );
};

export default function AdminVolunteerHours() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualEntries, setManualEntries] = useState([]);
  const [activeVolunteers, setActiveVolunteers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ volunteer_id: "", date: "", hours: "", description: "" });
  const [adding, setAdding] = useState(false);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      api.getVolunteerStats().then((r) => setData(r.data)),
      api.getManualHours().then((r) => setManualEntries(r.data.entries || [])),
      api.getVolunteers().then((r) => setActiveVolunteers(r.data.volunteers || [])),
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleAddManual = async (e) => {
    e.preventDefault();
    if (!addForm.volunteer_id || !addForm.date || !addForm.hours) return;
    setAdding(true);
    try {
      await api.addManualHours({
        volunteer_id: addForm.volunteer_id,
        date: addForm.date,
        hours: parseFloat(addForm.hours),
        description: addForm.description,
      });
      toast.success("Manual hours added");
      setShowAddModal(false);
      setAddForm({ volunteer_id: "", date: "", hours: "", description: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add hours");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteManual = async (id) => {
    try {
      await api.deleteManualHours(id);
      toast.success("Entry removed");
      fetchData();
    } catch {
      toast.error("Failed to delete entry");
    }
  };

  const summary    = data?.summary    || { total_confirmed: 0, total_hours: 0, avg_hours_per_volunteer: 0 };
  const volunteers = data?.volunteers || [];
  const monthly    = (data?.monthly_totals || []).slice(-6);

  const formatLastActive = (isoStr) => {
    if (!isoStr) return "Never";
    try {
      return new Date(isoStr).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" });
    } catch { return "—"; }
  };

  // Only show volunteers with bookings in the chart (keep all in table)
  const chartVols = volunteers.filter((v) => v.total_hours > 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Volunteer Hours</h1>
          <p className="text-slate-500 mt-1">Total time contributed by volunteers across all confirmed bookings</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowAddModal(true)}
            className="gap-2 border-teal-200 text-teal-700 hover:bg-teal-50"
            data-testid="add-manual-hours-btn"
          >
            <Plus className="w-4 h-4" /> Add Manual Hours
          </Button>
          <Button 
            variant="outline" 
            onClick={() => api.exportVolunteerHoursCSV().then(r => { const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "volunteer-hours.csv"; a.click(); URL.revokeObjectURL(url); })} 
            className="gap-2 border-slate-200 text-slate-600 hover:bg-slate-50" 
            data-testid="export-hours-btn"
          >
            <Download className="w-4 h-4" /> Export
          </Button>
          <Button variant="outline" onClick={fetchData} className="gap-2 border-green-200 text-green-700 hover:bg-green-50" data-testid="refresh-hours-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Hour calculation note */}
      <div className="mb-6 p-4 bg-teal-50 border border-teal-100 rounded-xl text-sm text-teal-800 flex items-start gap-2">
        <Clock className="w-4 h-4 mt-0.5 shrink-0 text-teal-600" />
        <span>{HOUR_NOTE}</span>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <SummaryCard icon={CalendarDays} label="Bookings Covered" value={summary.total_confirmed} unit="" color="green" testId="hours-total-confirmed" />
        <SummaryCard icon={Clock}        label="Total Volunteer Hours" value={summary.total_hours} unit="h" color="teal" testId="hours-total-hours" />
        <SummaryCard icon={Users}        label="Avg Hours per Volunteer" value={summary.avg_hours_per_volunteer} unit="h" color="blue" testId="hours-avg" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Hours by volunteer chart */}
        {chartVols.length > 0 && (
          <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden" data-testid="volunteer-hours-chart">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-green-700" />
              <div>
                <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Hours by Volunteer</h2>
                <p className="text-slate-500 text-sm">Total hours contributed per volunteer</p>
              </div>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="h-48 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-green-700" /></div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartVols} margin={VH_CHART_MARGIN} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={VH_AXIS_TICK} axisLine={false} tickLine={false}
                      tickFormatter={(v) => v.split(" ")[0]} />
                    <YAxis allowDecimals={false} tick={VH_AXIS_TICK} axisLine={false} tickLine={false} unit="h" />
                    <Tooltip content={<CustomTooltip />} cursor={VH_TOOLTIP_CURSOR} />
                    <Bar dataKey="total_hours" radius={VH_BAR_RADIUS} name="Total Hours">
                      {chartVols.map((entry, i) => <Cell key={entry.name || `vol-${i}`} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}

        {/* Monthly hours trend */}
        <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden" data-testid="monthly-hours-chart">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-teal-600" />
            <div>
              <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Monthly Hours Trend</h2>
              <p className="text-slate-500 text-sm">Total volunteer hours per month (last 6 months)</p>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="h-48 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthly} margin={VH_CHART_MARGIN} barCategoryGap="40%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={VH_AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={VH_AXIS_TICK} axisLine={false} tickLine={false} unit="h" />
                  <Tooltip content={<MonthlyTooltip />} cursor={VH_TOOLTIP_CURSOR} />
                  <Bar dataKey="total_hours" fill="#0d9488" radius={VH_BAR_RADIUS} name="Total Hours" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Detailed volunteer breakdown table */}
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden" data-testid="volunteer-hours-table">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Volunteer Breakdown</h2>
          <p className="text-slate-500 text-sm">All volunteers sorted by total hours contributed</p>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading...</div>
        ) : volunteers.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No volunteers found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Volunteer</th>
                  <th className="text-center px-4 py-3 font-semibold">Confirmed</th>
                  <th className="text-center px-4 py-3 font-semibold">Booking Time</th>
                  <th className="text-center px-4 py-3 font-semibold">Setup/Travel</th>
                  <th className="text-center px-4 py-3 font-semibold">Manual</th>
                  <th className="text-center px-4 py-3 font-semibold text-teal-700">Total Hours</th>
                  <th className="text-center px-4 py-3 font-semibold">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {volunteers.map((v, i) => (
                  <tr key={v.name} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} data-testid={`hours-row-${i}`}>
                    <td className="px-6 py-3">
                      <p className="font-semibold text-slate-800">{v.name}</p>
                      <p className="text-slate-400 text-xs">{v.email}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {v.confirmed > 0
                        ? <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold">{v.confirmed}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600 font-medium">
                      {v.booking_hours > 0 ? `${v.booking_hours}h` : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600 font-medium">
                      {v.overhead_hours > 0 ? `${v.overhead_hours}h` : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600 font-medium">
                      {(v.manual_hours || 0) > 0 ? `${v.manual_hours}h` : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {v.total_hours > 0
                        ? <span className="px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-xs font-bold">{v.total_hours}h</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-500 text-xs">
                      {formatLastActive(v.last_booking)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {summary.total_hours > 0 && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td className="px-6 py-3 font-bold text-slate-800 text-sm">Total</td>
                    <td className="px-4 py-3 text-center font-bold text-slate-800">{summary.total_confirmed}</td>
                    <td className="px-4 py-3 text-center font-bold text-slate-600">
                      {volunteers.reduce((s, v) => s + v.booking_hours, 0)}h
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-slate-600">
                      {volunteers.reduce((s, v) => s + v.overhead_hours, 0)}h
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-slate-600">
                      {volunteers.reduce((s, v) => s + (v.manual_hours || 0), 0)}h
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-3 py-1 bg-teal-600 text-white rounded-full text-xs font-bold">{summary.total_hours}h</span>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Manual Hours Log */}
      {manualEntries.length > 0 && (
        <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden mt-8" data-testid="manual-hours-log">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Manual Hours Log</h2>
            <p className="text-slate-500 text-sm">Hours added for events and activities outside the booking system</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Volunteer</th>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-center px-4 py-3 font-semibold">Hours</th>
                  <th className="text-left px-4 py-3 font-semibold">Description</th>
                  <th className="text-center px-3 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {manualEntries.map((e, i) => (
                  <tr key={e.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} data-testid={`manual-row-${e.id}`}>
                    <td className="px-6 py-3 font-medium text-slate-800">{e.volunteer_name}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{e.date}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-xs font-bold">{e.hours}h</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{e.description || "—"}</td>
                    <td className="px-3 py-3 text-center">
                      <button onClick={() => handleDeleteManual(e.id)} className="text-slate-300 hover:text-red-500 transition-colors" title="Delete" data-testid={`delete-manual-${e.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Manual Hours Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Add Manual Hours</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddManual} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Volunteer</Label>
              <select required value={addForm.volunteer_id} onChange={(e) => setAddForm({ ...addForm, volunteer_id: e.target.value })}
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:border-green-600"
                data-testid="manual-hours-volunteer">
                <option value="">Select volunteer...</option>
                {activeVolunteers.filter(v => v.active !== false).map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Date</Label>
              <Input type="date" required value={addForm.date} onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                className="h-10" data-testid="manual-hours-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Hours</Label>
              <Input type="number" required min="0.5" step="0.5" value={addForm.hours} onChange={(e) => setAddForm({ ...addForm, hours: e.target.value })}
                placeholder="e.g. 3" className="h-10" data-testid="manual-hours-hours" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Description</Label>
              <Input value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                placeholder="e.g. Phoenix Park charity event" className="h-10" data-testid="manual-hours-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button type="submit" disabled={adding} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="manual-hours-submit">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Hours"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, unit, color, testId }) {
  const c = {
    green: { bg: "bg-green-50", text: "text-green-700", border: "border-green-100" },
    teal:  { bg: "bg-teal-50",  text: "text-teal-700",  border: "border-teal-100"  },
    blue:  { bg: "bg-blue-50",  text: "text-blue-700",  border: "border-blue-100"  },
  }[color] || {};
  return (
    <div className="bg-white rounded-2xl border border-green-100 shadow-sm p-5" data-testid={testId}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${c.bg} ${c.border} border`}>
        <Icon className={`w-4 h-4 ${c.text}`} />
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}{unit}</p>
      <p className="text-slate-500 text-xs mt-0.5">{label}</p>
    </div>
  );
}
